import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import { BookOpen } from 'lucide-react';

import { API_BASE_URL } from '@/config/api';
const API = API_BASE_URL;

export default function AdminGuide() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(userRes.data);
        
        // Redirect if not admin
        if (!userRes.data.is_admin) {
          navigate('/dashboard');
        }
      } catch (error) {
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-amber-600 border-t-transparent mx-auto mb-4"></div>
          <p className="font-bebas text-xl tracking-wider">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Navigation user={user} />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-emerald-900 rounded-md p-8 mb-8 card-shadow text-white">
          <div className="flex items-center space-x-3 mb-2">
            <BookOpen size={40} />
            <h1 className="font-bebas text-5xl tracking-wider">
              Admin & SuperAdmin Guide
            </h1>
          </div>
          <p className="font-manrope text-emerald-100 text-lg">
            Complete reference for managing The Shamrock Pool
          </p>
        </div>

        {/* Guide Content */}
        <div className="bg-white rounded-md card-shadow p-8 font-manrope prose prose-emerald max-w-none">
          
          {/* Table of Contents */}
          <div className="bg-emerald-50 border-l-4 border-emerald-600 p-6 mb-8">
            <h2 className="font-bebas text-2xl text-emerald-900 mt-0 mb-4">Quick Navigation</h2>
            <ul className="space-y-2 mb-0">
              <li><a href="#roles" className="text-emerald-700 hover:text-emerald-900">Admin Roles Overview</a></li>
              <li><a href="#tournament" className="text-emerald-700 hover:text-emerald-900">Tournament Management</a></li>
              <li><a href="#users" className="text-emerald-700 hover:text-emerald-900">User Management</a></li>
              <li><a href="#teams" className="text-emerald-700 hover:text-emerald-900">Team & Game Management</a></li>
              <li><a href="#payments" className="text-emerald-700 hover:text-emerald-900">Payment Tracking</a></li>
              <li><a href="#email" className="text-emerald-700 hover:text-emerald-900">Email Broadcasting</a></li>
              <li><a href="#bestpractices" className="text-emerald-700 hover:text-emerald-900">Best Practices</a></li>
            </ul>
          </div>

          {/* Admin Roles */}
          <section id="roles" className="mb-12">
            <h2 className="font-bebas text-3xl text-emerald-900 border-b-2 border-emerald-200 pb-2">Admin Roles Overview</h2>
            
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div className="bg-blue-50 p-6 rounded-md border border-blue-200">
                <h3 className="font-bebas text-2xl text-blue-900 mt-0">Admin</h3>
                <p className="font-bold mb-2">Permissions:</p>
                <ul className="space-y-1 mb-0">
                  <li>✓ View all tournaments, users, participants</li>
                  <li>✓ Manage tournament settings</li>
                  <li>✓ Upload teams and manage games</li>
                  <li>✓ Update game results</li>
                  <li>✓ Track and confirm payments</li>
                  <li>✓ Send broadcast emails</li>
                </ul>
              </div>

              <div className="bg-amber-50 p-6 rounded-md border border-amber-300">
                <h3 className="font-bebas text-2xl text-amber-900 mt-0">SuperAdmin ⭐</h3>
                <p className="font-bold mb-2">Additional Permissions:</p>
                <ul className="space-y-1 mb-0">
                  <li>✓ Create new tournaments</li>
                  <li>✓ Complete/Archive/Delete tournaments</li>
                  <li>✓ Generate next rounds</li>
                  <li>✓ Delete/Regenerate rounds</li>
                  <li>✓ Edit semifinal configuration</li>
                  <li>✓ Promote/Demote admins</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Tournament Management */}
          <section id="tournament" className="mb-12">
            <h2 className="font-bebas text-3xl text-emerald-900 border-b-2 border-emerald-200 pb-2">Tournament Management</h2>
            
            <h3 className="font-bebas text-2xl text-emerald-800 mt-6">Creating a Tournament (SuperAdmin Only)</h3>
            <ol className="space-y-2">
              <li>Go to <strong>Admin Dashboard</strong></li>
              <li>Find the <strong>"Create Tournament"</strong> section</li>
              <li>Enter the <strong>Year</strong> (e.g., 2026)</li>
              <li>Enter a <strong>Name</strong> (e.g., "March Madness 2026")</li>
              <li>Configure <strong>Semifinal Pairings</strong>:
                <ul className="ml-6 mt-2">
                  <li>Semifinal 1: Region 1 vs Region 2 (e.g., East vs West)</li>
                  <li>Semifinal 2: Region 3 vs Region 4 (e.g., South vs Midwest)</li>
                </ul>
              </li>
              <li>Click <strong>"Create Tournament"</strong></li>
            </ol>

            <h3 className="font-bebas text-2xl text-emerald-800 mt-8">Configuring Payment Information</h3>
            <ol className="space-y-2">
              <li>Click <strong>"Configure Payment Info"</strong> button</li>
              <li>Enter:
                <ul className="ml-6 mt-2">
                  <li><strong>Entry Fee</strong> amount (e.g., 20)</li>
                  <li><strong>Venmo Username</strong> (e.g., @shamrock-pool)</li>
                  <li><strong>PayPal Username</strong> (optional)</li>
                  <li><strong>CashApp Username</strong> (optional)</li>
                </ul>
              </li>
              <li>Click <strong>"Save Payment Info"</strong></li>
            </ol>
            
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-4">
              <p className="mb-0"><strong>💡 Tip:</strong> Users will see this information on their Dashboard and can mark payments as sent.</p>
            </div>

            <h3 className="font-bebas text-2xl text-emerald-800 mt-8">Tournament Status Flow</h3>
            <div className="bg-gray-100 p-4 rounded-md">
              <p className="font-bold mb-2">Status Progression:</p>
              <p className="mb-0">
                <span className="bg-gray-300 px-3 py-1 rounded">Setup</span> 
                <span className="mx-2">→</span>
                <span className="bg-green-300 px-3 py-1 rounded">Active</span>
                <span className="mx-2">→</span>
                <span className="bg-blue-300 px-3 py-1 rounded">Completed</span>
                <span className="mx-2">→</span>
                <span className="bg-purple-300 px-3 py-1 rounded">Archived</span>
              </p>
            </div>
          </section>

          {/* User Management */}
          <section id="users" className="mb-12">
            <h2 className="font-bebas text-3xl text-emerald-900 border-b-2 border-emerald-200 pb-2">User Management</h2>
            
            <h3 className="font-bebas text-2xl text-emerald-800 mt-6">Promoting Users to Admin</h3>
            <ol className="space-y-2">
              <li>Find the user in the <strong>"User Management"</strong> section</li>
              <li>Click <strong>"Make Admin"</strong> button</li>
              <li>User gains admin permissions immediately</li>
            </ol>

            <h3 className="font-bebas text-2xl text-emerald-800 mt-8">Demoting Admins (SuperAdmin Only)</h3>
            <ol className="space-y-2">
              <li>Find the admin in the list (shows "ADMIN" badge)</li>
              <li>Click <strong>"Remove Admin"</strong> button</li>
              <li>User loses admin permissions</li>
            </ol>
            
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mt-4">
              <p className="mb-0"><strong>⚠️ Note:</strong> You cannot demote SuperAdmins through the UI.</p>
            </div>
          </section>

          {/* Teams & Games */}
          <section id="teams" className="mb-12">
            <h2 className="font-bebas text-3xl text-emerald-900 border-b-2 border-emerald-200 pb-2">Team & Game Management</h2>
            
            <h3 className="font-bebas text-2xl text-emerald-800 mt-6">Uploading Teams</h3>
            <ol className="space-y-2">
              <li>In <strong>"Manage Teams"</strong>, click <strong>"Add Teams Manually"</strong></li>
              <li>Enter teams in this format (one per line):
                <div className="bg-gray-800 text-green-400 p-4 rounded-md mt-2 font-mono text-sm">
                  Duke, 1, East<br/>
                  Gonzaga, 2, West<br/>
                  Kansas, 3, South<br/>
                  Purdue, 4, Midwest
                </div>
                <p className="text-sm mt-2 mb-0"><strong>Format:</strong> Team Name, Seed, Region</p>
              </li>
              <li>Click <strong>"Add Teams"</strong></li>
            </ol>

            <div className="bg-green-50 border-l-4 border-green-500 p-4 mt-4">
              <p className="font-bold mb-2">What Happens:</p>
              <ul className="space-y-1 mb-0">
                <li>✅ All 64 teams saved to database</li>
                <li>✅ Round 1 games (32 games) automatically generated</li>
                <li>✅ Bracket structure created with proper matchups</li>
                <li>✅ Tournament status changes to "active"</li>
              </ul>
            </div>

            <h3 className="font-bebas text-2xl text-emerald-800 mt-8">Updating Game Results</h3>
            <ol className="space-y-2">
              <li>Click <strong>"Show Games"</strong> to view all games</li>
              <li>Use <strong>Round Filter</strong> to view specific rounds</li>
              <li>For each game, click the <strong>"Set Winner"</strong> button for the winning team</li>
              <li>Game is marked as complete</li>
              <li>Scores update on the leaderboard automatically</li>
            </ol>

            <h3 className="font-bebas text-2xl text-emerald-800 mt-8">Generating Next Round (SuperAdmin Only)</h3>
            <ol className="space-y-2">
              <li>Ensure ALL games in current round are marked complete</li>
              <li>Click <strong>"Generate Next Round"</strong></li>
              <li>System creates next round games with winners</li>
            </ol>
          </section>

          {/* Payment Tracking */}
          <section id="payments" className="mb-12">
            <h2 className="font-bebas text-3xl text-emerald-900 border-b-2 border-emerald-200 pb-2">Payment Tracking</h2>
            
            <h3 className="font-bebas text-2xl text-emerald-800 mt-6">How It Works</h3>
            
            <div className="grid md:grid-cols-2 gap-6 mt-4">
              <div className="bg-blue-50 p-4 rounded-md">
                <h4 className="font-bold text-blue-900 mb-2">User Side:</h4>
                <ol className="space-y-1 text-sm mb-0">
                  <li>User goes to Dashboard</li>
                  <li>Sees "Entry Fee Payment" section</li>
                  <li>Sends payment via Venmo or Cash</li>
                  <li>Clicks "I've Sent Payment"</li>
                  <li>Selects payment method</li>
                  <li>Status: ⏳ Pending</li>
                </ol>
              </div>

              <div className="bg-green-50 p-4 rounded-md">
                <h4 className="font-bold text-green-900 mb-2">Admin Side:</h4>
                <ol className="space-y-1 text-sm mb-0">
                  <li>Click "Show Participants"</li>
                  <li>See list with payment status</li>
                  <li>Verify payment received</li>
                  <li>Click "Confirm Payment"</li>
                  <li>User status: ✓ Paid</li>
                </ol>
              </div>
            </div>

            <h3 className="font-bebas text-2xl text-emerald-800 mt-8">Payment Status Badges</h3>
            <ul className="space-y-2">
              <li><span className="bg-green-100 text-green-800 px-3 py-1 rounded font-bold">✓ PAID</span> - Payment confirmed by admin</li>
              <li><span className="bg-amber-100 text-amber-800 px-3 py-1 rounded font-bold">⏳ PENDING</span> - User marked as sent, awaiting confirmation</li>
              <li><span className="bg-red-100 text-red-800 px-3 py-1 rounded font-bold">⚠️ UNPAID</span> - No payment sent yet</li>
            </ul>

            <h3 className="font-bebas text-2xl text-emerald-800 mt-8">Confirming Payments</h3>
            <ol className="space-y-2">
              <li>Check your Venmo/bank account for payment</li>
              <li>Verify payment from that user</li>
              <li>Click <strong>"Confirm Payment"</strong></li>
              <li>User sees "✓ Paid" badge</li>
            </ol>
          </section>

          {/* Email Broadcasting */}
          <section id="email" className="mb-12">
            <h2 className="font-bebas text-3xl text-emerald-900 border-b-2 border-emerald-200 pb-2">Email Broadcasting</h2>
            
            <h3 className="font-bebas text-2xl text-emerald-800 mt-6">Sending Emails</h3>
            <ol className="space-y-2">
              <li>In <strong>"Send Broadcast Email"</strong> section</li>
              <li>Select recipients:
                <ul className="ml-6 mt-2">
                  <li><strong>All Users</strong> - Everyone</li>
                  <li><strong>Participants Only</strong> - Users who submitted picks</li>
                  <li><strong>Non-Participants</strong> - Users who haven't submitted picks</li>
                </ul>
              </li>
              <li>Enter <strong>Subject Line</strong></li>
              <li>Enter <strong>Email Content</strong></li>
              <li>Optional: Enable <strong>Test Mode</strong> and enter test emails</li>
              <li>Click <strong>"Send Email"</strong></li>
            </ol>

            <div className="bg-green-50 border-l-4 border-green-500 p-4 mt-4">
              <p className="font-bold mb-2">✅ Good Use Cases:</p>
              <ul className="space-y-1 mb-0">
                <li>Tournament start announcement</li>
                <li>Reminder to submit picks before deadline</li>
                <li>Payment reminders</li>
                <li>Round completion updates</li>
                <li>Final results and winner announcement</li>
              </ul>
            </div>
          </section>

          {/* Best Practices */}
          <section id="bestpractices" className="mb-12">
            <h2 className="font-bebas text-3xl text-emerald-900 border-b-2 border-emerald-200 pb-2">Best Practices & Tips</h2>
            
            <h3 className="font-bebas text-2xl text-emerald-800 mt-6">Tournament Setup Workflow</h3>
            <div className="bg-gradient-to-r from-emerald-50 to-blue-50 p-6 rounded-md">
              <ol className="space-y-2 mb-0">
                <li>✅ <strong>Create Tournament</strong> (SuperAdmin)</li>
                <li>✅ <strong>Configure Payment Info</strong> (Admin)</li>
                <li>✅ <strong>Send announcement email</strong> to users</li>
                <li>⏳ <strong>Wait for payments</strong> and confirm them</li>
                <li>✅ <strong>Upload 64 teams</strong> (creates Round 1 automatically)</li>
                <li>✅ <strong>Lock picks</strong> when tournament starts</li>
                <li>✅ <strong>Update game results</strong> as games complete</li>
                <li>✅ <strong>Generate next round</strong> (SuperAdmin)</li>
                <li>🔁 <strong>Repeat 7-8</strong> until tournament ends</li>
                <li>✅ <strong>Complete/Archive tournament</strong> (SuperAdmin)</li>
              </ol>
            </div>

            <h3 className="font-bebas text-2xl text-emerald-800 mt-8">Common Tasks Quick Reference</h3>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="bg-blue-50 p-4 rounded-md">
                <h4 className="font-bold mb-2">Weekly During Tournament:</h4>
                <ul className="space-y-1 text-sm mb-0">
                  <li>Update game results (2-3x/week)</li>
                  <li>Generate next round (1x/week)</li>
                  <li>Check payment status</li>
                  <li>Send email updates</li>
                </ul>
              </div>

              <div className="bg-amber-50 p-4 rounded-md">
                <h4 className="font-bold mb-2">Daily During Peak:</h4>
                <ul className="space-y-1 text-sm mb-0">
                  <li>Confirm pending payments</li>
                  <li>Answer user questions</li>
                  <li>Monitor leaderboard</li>
                </ul>
              </div>
            </div>

            <h3 className="font-bebas text-2xl text-emerald-800 mt-8">Troubleshooting</h3>
            <div className="space-y-3">
              <div className="bg-red-50 border-l-4 border-red-400 p-3">
                <p className="font-bold mb-1">Issue: Can't generate next round</p>
                <p className="text-sm mb-0">✅ Solution: Make sure ALL games in current round are marked complete</p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-3">
                <p className="font-bold mb-1">Issue: Wrong team advanced</p>
                <p className="text-sm mb-0">✅ Solution (SuperAdmin): Use "Regenerate Round" to fix</p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-3">
                <p className="font-bold mb-1">Issue: User says they paid but shows unpaid</p>
                <p className="text-sm mb-0">✅ Solution: Check if they clicked "I've Sent Payment" - if not, ask them to</p>
              </div>
            </div>
          </section>

          {/* Quick Reference Table */}
          <section className="mb-12">
            <h2 className="font-bebas text-3xl text-emerald-900 border-b-2 border-emerald-200 pb-2">Quick Command Reference</h2>
            
            <div className="overflow-x-auto mt-6">
              <table className="min-w-full bg-white border border-gray-300">
                <thead className="bg-emerald-100">
                  <tr>
                    <th className="px-4 py-2 border-b text-left font-bebas text-lg">Task</th>
                    <th className="px-4 py-2 border-b text-left font-bebas text-lg">Who</th>
                    <th className="px-4 py-2 border-b text-left font-bebas text-lg">Location</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-2 border-b">Create Tournament</td>
                    <td className="px-4 py-2 border-b"><span className="bg-amber-100 px-2 py-1 rounded text-xs">SuperAdmin</span></td>
                    <td className="px-4 py-2 border-b">Admin Dashboard → Create Tournament</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-2 border-b">Configure Payment</td>
                    <td className="px-4 py-2 border-b"><span className="bg-blue-100 px-2 py-1 rounded text-xs">Admin</span></td>
                    <td className="px-4 py-2 border-b">Admin Dashboard → Configure Payment Info</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-2 border-b">Upload Teams</td>
                    <td className="px-4 py-2 border-b"><span className="bg-blue-100 px-2 py-1 rounded text-xs">Admin</span></td>
                    <td className="px-4 py-2 border-b">Admin Dashboard → Add Teams Manually</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-2 border-b">Update Game Result</td>
                    <td className="px-4 py-2 border-b"><span className="bg-blue-100 px-2 py-1 rounded text-xs">Admin</span></td>
                    <td className="px-4 py-2 border-b">Admin Dashboard → Manage Games → Set Winner</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-2 border-b">Generate Next Round</td>
                    <td className="px-4 py-2 border-b"><span className="bg-amber-100 px-2 py-1 rounded text-xs">SuperAdmin</span></td>
                    <td className="px-4 py-2 border-b">Admin Dashboard → Generate Next Round</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-2 border-b">Confirm Payment</td>
                    <td className="px-4 py-2 border-b"><span className="bg-blue-100 px-2 py-1 rounded text-xs">Admin</span></td>
                    <td className="px-4 py-2 border-b">Admin Dashboard → Payment Tracking → Confirm</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-2 border-b">Send Email</td>
                    <td className="px-4 py-2 border-b"><span className="bg-blue-100 px-2 py-1 rounded text-xs">Admin</span></td>
                    <td className="px-4 py-2 border-b">Admin Dashboard → Send Broadcast Email</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-2 border-b">Promote to Admin</td>
                    <td className="px-4 py-2 border-b"><span className="bg-blue-100 px-2 py-1 rounded text-xs">Admin</span></td>
                    <td className="px-4 py-2 border-b">Admin Dashboard → User Management</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Footer */}
          <div className="bg-emerald-900 text-white p-6 rounded-md text-center">
            <p className="font-bebas text-2xl mb-2">Need Help?</p>
            <p className="mb-0">Contact your SuperAdmin: pkennedyvt@gmail.com</p>
          </div>

        </div>
      </div>
    </div>
  );
}
